import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Row, Col, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { authService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PASSWORD_MIN } from '../../utils/password';

const { Title } = Typography;

const MiPerfil = () => {
  const { user, updateUser } = useAuth();
  const [formDatos] = Form.useForm();
  const [formPassword] = Form.useForm();
  // Un estado por form: sin esto, el doble click en "Cambiar contraseña" manda
  // dos POST. El primero cambia la clave; el segundo llega con la vieja como
  // `password_actual` y devuelve 401 — la clave SÍ cambió, pero la pantalla
  // dice que no, y el usuario reintenta con la anterior.
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const [guardandoPassword, setGuardandoPassword] = useState(false);

  useEffect(() => {
    if (user) {
      formDatos.setFieldsValue({
        nom_usuario: user.nom_usuario,
        email_usuario: user.email_usuario,
      });
    }
  }, [user, formDatos]);

  const handleGuardarDatos = async (values) => {
    setGuardandoDatos(true);
    try {
      const response = await authService.updatePerfil({
        nom_usuario: values.nom_usuario,
        email_usuario: values.email_usuario,
      });
      updateUser({
        nom_usuario: response.data.nom_usuario,
        email_usuario: response.data.email_usuario,
      });
      message.success('Datos actualizados correctamente');
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al actualizar los datos';
      message.error(msg);
    } finally {
      setGuardandoDatos(false);
    }
  };

  const handleCambiarPassword = async (values) => {
    setGuardandoPassword(true);
    try {
      await authService.cambiarPassword({
        password_actual: values.password_actual,
        password_nuevo: values.password_nuevo,
      });
      // El backend apaga `debe_cambiar_password` en TODO cambio de clave
      // propio, así que el estado local tiene que reflejarlo o queda mintiendo
      // hasta la próxima recarga. Es el mismo motivo por el que lo hace
      // `CambioClaveObligatorio`.
      updateUser({ debe_cambiar_password: false });
      message.success('Contraseña actualizada correctamente');
      formPassword.resetFields();
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al cambiar la contraseña';
      message.error(msg);
    } finally {
      setGuardandoPassword(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 0' }}>
      <Title level={3} style={{ marginBottom: 24 }}>Mi Perfil</Title>

      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Card title="Datos personales">
            <Form
              form={formDatos}
              layout="vertical"
              onFinish={handleGuardarDatos}
            >
              <Form.Item
                label="Nombre de usuario"
                name="nom_usuario"
                rules={[{ required: true, message: 'El nombre es requerido' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Nombre de usuario" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email_usuario"
                rules={[
                  { required: true, message: 'El email es requerido' },
                  { type: 'email', message: 'Ingresá un email válido' },
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Email" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={guardandoDatos}>
                  Guardar cambios
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Cambiar contraseña">
            <Form
              form={formPassword}
              layout="vertical"
              onFinish={handleCambiarPassword}
            >
              <Form.Item
                label="Contraseña actual"
                name="password_actual"
                rules={[{ required: true, message: 'Ingresá tu contraseña actual' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Contraseña actual" />
              </Form.Item>

              <Form.Item
                label="Nueva contraseña"
                name="password_nuevo"
                rules={[
                  { required: true, message: 'Ingresá la nueva contraseña' },
                  { min: PASSWORD_MIN, message: `La contraseña debe tener al menos ${PASSWORD_MIN} caracteres` },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Nueva contraseña" />
              </Form.Item>

              <Form.Item
                label="Confirmar nueva contraseña"
                name="password_confirmar"
                dependencies={['password_nuevo']}
                rules={[
                  { required: true, message: 'Confirmá la nueva contraseña' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password_nuevo') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Las contraseñas no coinciden'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder="Confirmar nueva contraseña" />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" loading={guardandoPassword}>
                  Cambiar contraseña
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MiPerfil;
